// Sibling-barrel leaf imports (kept relative, not `#shared`) — this module IS
// part of `#shared`, so importing the top barrel would be a self-cycle.
import { reportThrottled } from '../sentry';

import { isNumericIdString } from './isNumericIdString';

/**
 * Session-scoped monotonic cursor for the domain-event feed: the largest applied
 * `DomainEventFrame.id` (a bigint carried as a decimal string). MUST NOT be
 * persisted — persisting would falsely signal "caught up" after retention prunes
 * the rows it was derived from. Distinct from the timestamp-based case cursor.
 */
let seq = '';

/**
 * Epoch ms of the last frame that reached `advanceSeq` (TMDXNC-14), i.e. every
 * LIVE socket frame and every since-seq catch-up frame — both route through
 * `applyDomainEventFrame`, which calls `advanceSeq`. Two paths deliberately do
 * NOT stamp: the cold-start lookback seed (it loads the window through RTK Query
 * and calls `seedSeq`), and a frame dropped because the cache entry is missing
 * (`applyDomainEventFrame` returns before advancing). So a client that just
 * cold-started with a full window still reads `null` here.
 *
 * `null` means "no frame yet this session" — NOT time zero. Callers must supply
 * their own baseline rather than treating it as an epoch timestamp; the silence
 * check's policy for that case lives with the check, not here.
 */
let lastAdvanceAt: number | null = null;

const REPORT_THROTTLE_MS = 60_000;

/**
 * A malformed id reaching the cursor means an upstream guard (the domain-event
 * schema) has regressed — the frame should never have gotten this far. Report
 * it (throttled) so the regression is debuggable instead of silently swallowed.
 */
const reportMalformedId = (id: string): void => {
  reportThrottled(
    'liveSync:domain-event-cursor-malformed-id',
    'liveSync: ignored malformed domain-event cursor id',
    {
      throttleMs: REPORT_THROTTLE_MS,
      level: 'warning',
      extra: { id },
    },
  );
};

/** Current cursor, or `''` if none applied yet. */
export const getSeq = (): string => seq;

/** Move the cursor up to `id` if numerically greater. Malformed/empty ignored. */
export const advanceSeq = (id: string): void => {
  if (!isNumericIdString(id)) {
    reportMalformedId(id);
    return;
  }
  if (seq === '' || BigInt(id) > BigInt(seq)) {
    seq = id;
  }
  // Stamped even when the id was a duplicate/older one: the point is "a frame
  // reached us", which is liveness, not cursor progress.
  lastAdvanceAt = Date.now();
};

/**
 * Epoch ms of the last frame that reached `advanceSeq`, or `null` if none yet
 * this session (see the note on `lastAdvanceAt` for which paths do not stamp).
 */
export const getLastAdvanceAt = (): number | null => lastAdvanceAt;

/** Set the cursor only if currently unset (first lookback seed). */
export const seedSeq = (id: string): void => {
  if (!isNumericIdString(id)) {
    reportMalformedId(id);
    return;
  }
  if (seq === '') {
    seq = id;
  }
};

/** Reset (tests / teardown). */
export const clearSeq = (): void => {
  seq = '';
  lastAdvanceAt = null;
};
